/*
 * Carrying a walkthrough across the surface switch.
 *
 * Ada and the Console are alternative renders of one document, not nested
 * views, so starting a tour unmounts the component that started it. React state
 * cannot survive that; sessionStorage can, and it is the right lifetime — a
 * walkthrough belongs to this tab and this sitting, and should not still be
 * waiting in a window opened tomorrow.
 *
 * Every card that can start a walkthrough shows where it stands, so the store
 * also tells its readers when it changes (useTour), and keeps how each recent
 * walkthrough ended.
 */

import { useSyncExternalStore } from "react";

const KEY = "nabx.ada.tour.v1";

/* How recent walkthroughs ended, newest first: which card started each, and
   per step whether the appliance showed it done. What a finished walkthrough
   left behind is the card's to say, after the walkthrough itself is gone. */
const ENDS_KEY = "nabx.ada.tourends.v1";
const MAX_ENDS = 12;

/* What a finished walkthrough leaves behind for the assistant to pick up.
 *
 * Applying by hand and applying by button are the same act, and the operator
 * should not have to do the second after doing the first. This carries the
 * changes that were confirmed live, so Ada can verify and report them on the
 * way back instead of still asking for a confirmation that is already true. */
const DONE_KEY = "nabx.ada.tourdone.v1";

/* A question the operator asked from inside the walkthrough, for the assistant
 * to pick up when it mounts. Same one-shot handoff as DONE_KEY. */
const ASK_KEY = "nabx.ada.tourask.v1";

const listeners = new Set();
const changed = () => listeners.forEach((fn) => fn());

/** subscribeTour calls fn whenever the walkthrough or its endings change. */
export function subscribeTour(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** startTour stores a new walkthrough. origin names the card that started it,
    { kind: "tour" | "change", id }, so that card can show where it stands. */
export function startTour(steps, origin = null) {
  if (!steps || !steps.length) return null;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ steps, at: 0, paused: false, origin }));
  } catch {
    return null; // private mode — the caller falls back to Apply
  }
  changed();
  return steps[0];
}

export function readTour() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const t = JSON.parse(raw);
    return t && Array.isArray(t.steps) && t.steps.length ? t : null;
  } catch {
    return null;
  }
}

export function advanceTour(at) {
  const t = readTour();
  if (!t) return null;
  if (at >= t.steps.length) return endTour();
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...t, at }));
  } catch {
    /* nothing to do — the tour continues from memory this session */
  }
  changed();
  return { ...t, at };
}

/* Stepping away from a walkthrough without losing it.
 *
 * Leaving for the assistant used to END the tour: "Back to Ada" called endTour,
 * so asking one question about step two cost the other four, and there was no
 * way back to the place you were standing. That made the assistant and the
 * walkthrough mutually exclusive, which is exactly backwards — the reason to
 * open one is usually a question only the other can answer.
 *
 * Paused rather than simply left running, because the console is also somewhere
 * the operator goes to do unrelated work. A tour resumes when they say so, not
 * because they happened to navigate back.
 */
export function pauseTour() {
  const t = readTour();
  if (!t) return null;
  if (t.paused) return t;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...t, paused: true }));
  } catch {
    /* it stays running in this tab — better than losing the plan */
  }
  changed();
  return { ...t, paused: true };
}

export function resumeTour() {
  const t = readTour();
  if (!t) return null;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...t, paused: false }));
  } catch {
    /* nothing to do — the caller renders from the value returned here */
  }
  changed();
  return { ...t, paused: false };
}

/** restartTour puts the walkthrough back on its first step, running. */
export function restartTour() {
  const t = readTour();
  if (!t) return null;
  const next = { ...t, at: 0, paused: false };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* the caller renders from the value returned here */
  }
  changed();
  return next;
}

/** recordTourEnd keeps how a finished walkthrough ended: steps as
    [{ id, title, done }], done true or false where the appliance was read and
    null where there was nothing to check. A newer ending of the same
    walkthrough replaces the older one. */
export function recordTourEnd(tour, steps) {
  if (!tour) return;
  const key = endKey(tour);
  const entry = { origin: tour.origin || null, at: Date.now(), steps };
  try {
    const kept = readTourEnds().filter((e) => endKey(e) !== key);
    sessionStorage.setItem(ENDS_KEY, JSON.stringify([entry, ...kept].slice(0, MAX_ENDS)));
  } catch {
    return; // the card offers the walkthrough again, which is the safe way to be wrong
  }
  changed();
}

// A walkthrough started before cards named their origin is keyed by its steps.
const endKey = (t) => t.origin?.id || (t.steps || []).map((s) => s.id).join(",");

export function readTourEnds() {
  try {
    const list = JSON.parse(sessionStorage.getItem(ENDS_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/* useSyncExternalStore needs the same object back while nothing changed, so
   the snapshot is parsed again only when the stored text differs. */
let cache = { raw: null, value: { tour: null, ends: [] } };

const snapshot = () => {
  let raw = "";
  try {
    raw = `${sessionStorage.getItem(KEY) || ""}\u0000${sessionStorage.getItem(ENDS_KEY) || ""}`;
  } catch {
    /* private mode — nothing is stored */
  }
  if (raw !== cache.raw) cache = { raw, value: { tour: readTour(), ends: readTourEnds() } };
  return cache.value;
};

/** useTour is the stored walkthrough and the recent endings, kept current. */
export const useTour = () => useSyncExternalStore(subscribeTour, snapshot, snapshot);

/** walkthroughOf is the stored walkthrough when it belongs to a card: the
    card that started it, or one whose change is one of its steps. */
export function walkthroughOf(tour, { origin, ids = [] }) {
  if (!tour) return null;
  if (origin && tour.origin?.id === origin) return tour;
  return ids.length && tour.steps.some((s) => ids.includes(s.id)) ? tour : null;
}

/** endingOf is the latest ending that belongs to a card, the same way. */
export function endingOf(ends, { origin, ids = [] }) {
  return (ends || []).find((e) =>
    (origin && e.origin?.id === origin) ||
    (ids.length && (e.steps || []).some((s) => ids.includes(s.id)))) || null;
}

/** askAboutStep hands the assistant an opening question. send asks it at
    once, for words the operator wrote themselves; otherwise it waits in the
    composer to be sent or edited. */
export function askAboutStep(question, { send = false } = {}) {
  const q = typeof question === "string" ? question.trim() : "";
  if (!q) return;
  try {
    sessionStorage.setItem(ASK_KEY, JSON.stringify({ q, send }));
  } catch {
    /* the operator can still type it themselves */
  }
}

/** takeTourQuestion returns and clears that question, { q, send }, read
    exactly once. */
export function takeTourQuestion() {
  try {
    const raw = sessionStorage.getItem(ASK_KEY);
    sessionStorage.removeItem(ASK_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return typeof v?.q === "string" ? { q: v.q, send: !!v.send } : null;
  } catch {
    return null;
  }
}

/** recordApplied hands the assistant the changes a walkthrough confirmed. */
export function recordApplied(proposals) {
  if (!proposals || !proposals.length) return;
  try {
    sessionStorage.setItem(DONE_KEY, JSON.stringify(proposals));
  } catch {
    /* the card stays actionable; nothing is lost but the acknowledgement */
  }
}

/** takeApplied returns and clears that handoff — it is read exactly once. */
export function takeApplied() {
  try {
    const raw = sessionStorage.getItem(DONE_KEY);
    sessionStorage.removeItem(DONE_KEY);
    const list = raw ? JSON.parse(raw) : null;
    return Array.isArray(list) && list.length ? list : null;
  } catch {
    return null;
  }
}

export function endTour() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* already gone */
  }
  changed();
  return null;
}
